import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { getIssues, getOrCreateTask, getStageTasks } from '../../../../../../../helpers/apiCalls';
import useTemplateState from '../../../../../../../../Template/atoms/variables';
import { useMsal } from '@azure/msal-react';
import Table from '../../../../../../../../Template/layoutComponents/Table';
import { parseIsoDateString } from '../../../../../../../../helpers/misc';
import TableTip from '../../../../../../../../Template/layoutComponents/Table/components/tip';

import './style.css';
import BypassButton from '../../helperComponents/BypassButton';

const WebFormOpenner = ({ data, setAllTasks, phaseTitle }) => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { assetId, interconnectionId } = useParams();

  const { setPendingApiCalls } = useTemplateState();

  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [forms, setForms] = useState(data);
  const [foundTasks, setFoundTasks] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setPendingApiCalls((current) => current.concat(['getStageTasks']));
    try {
      const tasks = await getStageTasks(instance, {
        stage: { in: forms.map((form) => form.phase) },
        test: { in: forms.map((form) => form.stage?._id) },
        'subject._id': assetId ?? interconnectionId,
        'subject._type': assetId ? 'Asset' : 'Interconnection',
      });

      if (tasks.items?.length > 0) {
        setFoundTasks(tasks.items);
        setAllTasks((prev) => [
          ...prev,
          ...forms.map((form) => {
            const status = tasks.items?.find((task) => task.test._id === form.stage._id)?.status;
            return {
              test: { _id: form?.stage?._id },
              report: {
                status:
                  status?.toLowerCase() === 'validated' ||
                  status?.toLowerCase() === 'approved' ||
                  status?.toLowerCase() === 'fail'
                    ? 'approved'
                    : 'pending',
              },
            };
          }),
        ]);
      } else {
        const tasks = await Promise.all(
          forms.map((form) =>
            getOrCreateTask(instance, {
              stage: form.phase,
              test: form.stage?._id,
              subjectId: assetId ?? interconnectionId,
              subjectType: assetId ? 'Asset' : 'Interconnection',
              token: `${assetId ?? interconnectionId}-${phaseTitle}-WEBFORM-${form?.stage?._id}`,
              code: 'WEBFORM',
            }),
          ),
        );
        setFoundTasks(tasks);

        setAllTasks((prev) => [
          ...prev,
          ...forms.map((form) => ({
            test: { _id: form?.stage?._id },
            report: { status: 'pending' },
          })),
        ]);
      }
    } catch (error) {
      console.log('Error fetching tasks:', error);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'getStageTasks'));
    }
  };

  const handleOpenForm = (data) => {
    navigate(`${location.pathname}/web-form`.replace(/\/\/web-form$/, '/web-form'), {
      state: { formData: data, stage: phaseTitle },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className='pageTitle'>{t('forms')}</div>
      <Table
        handleSelect={null}
        handleRowClick={null}
        apiCall={(instance, query) => {
          return {
            page: query.page,
            limit: query.limit,
            totalItems: forms.length,
            totalPages: Math.ceil(forms.length / query.limit),
            items: forms
              .slice(query.limit * (query.page - 1), query.limit * query.page)
              .map((item) => {
                return {
                  _id: item?.stage?._id,
                  type: item.stage[i18n.language].title,
                  executor: foundTasks?.find((task) => task?.test._id === item?.stage?._id)
                    ?.employee?.email,
                  date: foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.createdAt,
                  status: foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.status,
                  action: 'placeholder',
                };
              }),
          };
        }}
        loadingSkeleton={forms.length === 0}
        items={forms.map((item) => {
          return {
            _id: item?.stage?._id,
            type: item.stage[i18n.language].title,
            executor: foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.employee
              ?.email,
            date: foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.createdAt,
            status: foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.bypass
              ?.bypassed
              ? 'Bypass'
              : foundTasks?.find((task) => task?.test._id === item?.stage?._id)?.status,
            action: 'placeholder',
          };
        })}
        startingTotalItems={forms.length}
        tableSettings={{
          columnsConfig: {
            type: {
              label: t('type'),
            },
            executor: {
              label: t('executor'),
            },
            date: {
              label: t('date'),
              customElement: (row) => {
                return parseIsoDateString(row.date);
              },
            },
            status: {
              label: t('status'),
              customElement: (row) => {
                return <TableTip children={row.status} />;
              },
            },
            action: {
              label: t('action'),
              customElement: (row) => {
                return (
                  <div className='formTableButtons'>
                    <Button
                      size='large'
                      style={{
                        backgroundColor: '#1777FF',
                        border: '#1777FF',
                        color: 'white',
                        opacity:
                          foundTasks?.find((task) => (task?.test._id ?? task?.test) === row?._id)
                            ?.status !== 'Pending'
                            ? 0.5
                            : 1,
                      }}
                      disabled={
                        foundTasks?.find((task) => (task?.test._id ?? task?.test) === row?._id)
                          ?.status !== 'Pending'
                      }
                      onClick={() => {
                        const form = forms.find((form) => form?.stage?._id === row?._id);
                        console.log(form);
                        if (!form) return;
                        handleOpenForm(form?.stage);
                      }}
                    >
                      {t('fill_checklist')}
                    </Button>
                    <BypassButton
                      systemTask={foundTasks?.find(
                        (task) => (task?.test._id ?? task?.test) === row?._id,
                      )}
                      title={row?.type}
                      bypassCallback={() => {
                        fetchTasks();
                      }}
                    />
                  </div>
                );
              },
            },
          },
        }}
      />
    </div>
  );
};

export default WebFormOpenner;
